// Centralised chart configuration for Kalpa.
// The app uses recharts (not Chart.js), so helpers below return props meant
// for <XAxis>, <YAxis>, <Tooltip> and <Line>/<Bar> rather than a Chart.js
// options object. The METRIC_REGISTRY keeps unit/colour/axis decisions in one
// place so every chart in the app renders the same metric the same way.

export const METRIC_REGISTRY = {
  hrv_rmssd:           { unit: 'ms',     label: 'HRV (ms)',          color: '#3B82F6', axisGroup: 'left',  min: 0,    max: null },
  resting_heart_rate:  { unit: 'bpm',    label: 'HR (bpm)',          color: '#EF4444', axisGroup: 'left',  min: 40,   max: 220  },
  heart_rate:          { unit: 'bpm',    label: 'HR (bpm)',          color: '#EF4444', axisGroup: 'left',  min: 40,   max: 220  },
  spo2:                { unit: '%',      label: 'SpO2 (%)',          color: '#10B981', axisGroup: 'right', min: 88,   max: 100  },
  steps:               { unit: 'steps',  label: 'Steps',             color: '#8B5CF6', axisGroup: 'right', min: 0,    max: null },
  glucose:             { unit: 'mg/dL',  label: 'Glucose (mg/dL)',   color: '#F59E0B', axisGroup: 'right', min: 0,    max: null },
  calories:            { unit: 'kcal',   label: 'Calories (kcal)',   color: '#F97316', axisGroup: 'right', min: 0,    max: null },
  weight:              { unit: 'kg',     label: 'Weight (kg)',       color: '#6366F1', axisGroup: 'left',  min: null, max: null },
};

// Fallback used when a series can't be matched to the registry.
export const FALLBACK_METRIC = { unit: '', label: 'Value', color: '#8B5CF6', axisGroup: 'left', min: null, max: null };

// Loose name→key matcher so existing series names ("Resting Heart Rate",
// "HRV (RMSSD)", "Daily Steps", "SpO2") map to registry entries without
// having to rename every call site.
const KEY_SYNONYMS = {
  hrv_rmssd: /\bhrv\b|rmssd/i,
  resting_heart_rate: /resting.?heart.?rate|resting.?hr/i,
  heart_rate: /\bheart.?rate\b|\bhr\b|\bbpm\b/i,
  spo2: /\bspo2\b|blood.?oxygen|\boxygen.?sat/i,
  steps: /\bsteps?\b/i,
  glucose: /\bglucose\b|hba1c/i,
  calories: /\bcal(ories|s)?\b|\bkcal\b/i,
  weight: /\bweight\b|\bkg\b|\blbs\b/i,
};

export function resolveMetric(keyOrName) {
  if (!keyOrName) return null;
  const direct = METRIC_REGISTRY[keyOrName];
  if (direct) return { key: keyOrName, ...direct };
  const lc = String(keyOrName).toLowerCase();
  for (const [k, re] of Object.entries(KEY_SYNONYMS)) {
    if (re.test(lc)) return { key: k, ...METRIC_REGISTRY[k] };
  }
  return null;
}

// Returns axis props for recharts <YAxis>. Caller spreads into <YAxis>.
export function yAxisPropsFor(metricKeys, side = 'left') {
  const keys = Array.isArray(metricKeys) ? metricKeys : [metricKeys];
  const metrics = keys.map(resolveMetric).filter(Boolean).filter(m => m.axisGroup === side);
  if (metrics.length === 0) return null;
  const mins = metrics.map(m => m.min).filter(v => v != null);
  const maxs = metrics.map(m => m.max).filter(v => v != null);
  const label = metrics.map(m => m.label).join(' · ');
  return {
    yAxisId: side,
    orientation: side,
    domain: [
      mins.length ? Math.min(...mins) : 'auto',
      maxs.length ? Math.max(...maxs) : 'auto',
    ],
    label: { value: label, angle: -90, position: side === 'left' ? 'insideLeft' : 'insideRight', style: { fontSize: 11, fill: '#8B6914' } },
  };
}

// Returns props for a recharts <Line>/<Bar> dataset for a given metric.
// `dataKey` defaults to the registry key but can be overridden when the
// upstream data uses a different field name.
export function datasetPropsFor(metricKey, opts = {}) {
  const m = resolveMetric(metricKey) || FALLBACK_METRIC;
  return {
    dataKey: opts.dataKey || metricKey,
    name: opts.name || m.label,
    stroke: m.color,
    fill: m.color,
    yAxisId: m.axisGroup,
    dot: opts.dot ?? false,
    strokeWidth: opts.strokeWidth ?? 2,
    isAnimationActive: false,
  };
}

// Tooltip formatter that adds the registry unit to the displayed value.
// Pass as `<Tooltip formatter={tooltipFormatter(metricKey)} />` for single
// metric charts, or use `tooltipFormatterMulti(seriesNameToKey)` when one
// chart mixes several metrics.
export function tooltipFormatter(metricKey) {
  const m = resolveMetric(metricKey) || FALLBACK_METRIC;
  return (value) => [`${value} ${m.unit}`.trim(), m.label];
}

export function tooltipFormatterMulti(seriesNameToKey = {}) {
  return (value, name) => {
    const k = seriesNameToKey[name] || name;
    const m = resolveMetric(k) || FALLBACK_METRIC;
    return [`${value} ${m.unit}`.trim(), m.label || name];
  };
}

// Build a complete chart "options" bundle for a multi-metric chart. The
// returned object is a convenience wrapper — destructure pieces into JSX:
//
//   const cfg = buildChartOptions(['steps','resting_heart_rate'], 'Activity');
//   <LineChart ...>
//     <XAxis dataKey="date" />
//     {cfg.leftAxis  && <YAxis {...cfg.leftAxis}  tick={{fontSize:10,fill:'#8B6914'}} />}
//     {cfg.rightAxis && <YAxis {...cfg.rightAxis} tick={{fontSize:10,fill:'#8B6914'}} />}
//     <Tooltip formatter={cfg.tooltipFormatter} />
//     {cfg.series.map(s => <Line {...s} key={s.dataKey} type="monotone" />)}
//   </LineChart>
export function buildChartOptions(metricKeys, title) {
  const keys = Array.isArray(metricKeys) ? metricKeys : [metricKeys];
  return {
    title: title || '',
    leftAxis:  yAxisPropsFor(keys, 'left'),
    rightAxis: yAxisPropsFor(keys, 'right'),
    series: keys.map(k => datasetPropsFor(k)),
    tooltipFormatter: tooltipFormatterMulti(Object.fromEntries(keys.map(k => {
      const m = resolveMetric(k) || FALLBACK_METRIC;
      return [m.label, k];
    }))),
  };
}

// Convenience: returns the colour for a metric key/name. Used by sparklines
// and one-off chart sites that don't need the full registry plumbing.
export function colorFor(metricKey) {
  const m = resolveMetric(metricKey) || FALLBACK_METRIC;
  return m.color;
}

// Convenience: returns the unit for a metric key/name.
export function unitFor(metricKey) {
  const m = resolveMetric(metricKey) || FALLBACK_METRIC;
  return m.unit;
}
